import { User as AuthUser, onAuthStateChanged, signInWithCustomToken, signOut } from 'firebase/auth'
import { doc, onSnapshot } from 'firebase/firestore'
import localforage from 'localforage'
import posthog from 'posthog-js'
import { createContext, Dispatch, ReactNode, SetStateAction, useContext, useEffect, useMemo, useRef, useState } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { auth, db } from 'src/services/firebase-compat'
import { metricsService } from 'src/services/metrices'
import { get } from 'src/utils/config/lodashUtils'
import { validateUserToken } from 'src/utils/firebaseFunctions/validateUserToken'
import { createSessionCookie } from 'src/utils/firebaseFunctions/createSessionCookie'
import { clearSessionCookie } from 'src/utils/firebaseFunctions/clearSessionCookie'
import { checkAuthStatus } from 'src/utils/firebaseFunctions/checkAuthStatus'
import { getSession, setSession } from 'src/utils/functions/localStorage'
import { PRODUCTION_DOMAIN } from 'src/utils/config'
import useValidateLocalData from 'src/utils/hooks/useValidateLocalData'
import { PageDetails, User } from './AuthContext.type'
import { useErrorData } from './ErrorContext'
import filterGraphManager from './NewFilterContext/utils/FilterGraphManager'
import { pwaManager } from 'src/utils/sw/pwaUtils'

interface PublicAuthContextInterface {
  currentUser: User | null
  authCurrentUser: AuthUser | null
  pageDetails: PageDetails
  loading: boolean
  isLoggingOut: boolean
  magicLinkFromAdmin: boolean
  setLoading: Dispatch<SetStateAction<boolean>>
  setPageDetails: Dispatch<SetStateAction<PageDetails>>
  logout: () => void
  analyticsReset: () => void
}

const PublicAuthContext = createContext({} as PublicAuthContextInterface)

export const usePublicAuthContext = (): PublicAuthContextInterface => useContext(PublicAuthContext)

interface PublicAuthContextProviderProps {
  children: ReactNode
}

export default function PublicAuthContextProvider(props: PublicAuthContextProviderProps): JSX.Element {
  const { refreshNeeded, localData, setLocalData } = useValidateLocalData()
  const navigate = useNavigate()
  const { asyncWrapper, handleError } = useErrorData()
  const [params, setParams] = useSearchParams()
  const [tempLoading, setLoading] = useState(true)
  const [magicLinkLoading, setMagicLinkLoading] = useState(false)
  const [magicLinkFromAdmin, setMagicLinkFromAdmin] = useState(false)
  const [currentUser, setCurrentUser] = useState<User | null>(null)
  const [authCurrentUser, setAuthCurrentUser] = useState<AuthUser | null>(null)
  const [isLoggingOut, setIsLoggingOut] = useState(false)
  const [pageDetails, setPageDetails] = useState<PageDetails>({
    path: location.pathname,
    visitTime: new Date().getTime()
  })
  const provisioningWarningShown = useRef(false)
  const sessionCookieCreated = useRef(false)

  const loading = useMemo(() => {
    return tempLoading || magicLinkLoading
  }, [tempLoading, magicLinkLoading])

  const analyticsReset = () => {
    posthog.reset()
  }

  const clearLocalData = () => {
    localStorage.clear()
    sessionStorage.clear()
    localforage
      .clear()
      .then(() => {})
      .catch((err) => {
        console.log('Failed to clear localforage: ', err.message)
      })
    filterGraphManager.clearGraphs()
    setLocalData(false)
    analyticsReset()
  }

  const logout = () => {
    asyncWrapper(
      (async () => {
        // Show "Logging out..." state
        setIsLoggingOut(true)

        try {
          // Clear session cookie for universal logout across all Loop platforms
          await clearSessionCookie()

          // Clear all service worker caches BEFORE signing out
          // This prevents stale cached JavaScript from causing infinite reload loops
          // when the user logs back in
          // Note: We clear regardless of PWA enabled state - old service workers may exist
          // from previous deployments when PWA was enabled
          if ('serviceWorker' in navigator) {
            const registrations = await navigator.serviceWorker.getRegistrations()
            if (registrations.length > 0) {
              console.log('[Logout] Found', registrations.length, 'service worker(s), clearing caches...')
              await pwaManager.cache.clearAllCaches(false, false)
              console.log('[Logout] Service worker caches cleared')
            }
          }

          setCurrentUser(undefined)
          await signOut(auth)
          setAuthCurrentUser(null)
          clearLocalData()

          // Hard refresh to ensure fresh code is loaded
          // This guarantees the user gets the latest JavaScript after logout
          window.location.href = window.location.origin
        } finally {
          setIsLoggingOut(false)
        }
      })()
    )
  }

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (currentUser) => {
      provisioningWarningShown.current = false
      setAuthCurrentUser(currentUser)
      if (!currentUser && !magicLinkLoading) {
        setCurrentUser(undefined)
        setLoading(false)
        sessionCookieCreated.current = false // Reset on logout
      } else if (currentUser && !sessionCookieCreated.current) {
        // Create session cookie for SSO across all Loop platforms
        try {
          const idToken = await currentUser.getIdToken()
          await createSessionCookie(idToken)
          sessionCookieCreated.current = true
        } catch (err) {
          console.error('Failed to create session cookie:', err)
        }
      }
    })
    return () => {
      if (unsubscribe) {
        unsubscribe()
      }
    }
  }, [refreshNeeded, localData, magicLinkLoading])

  useEffect(() => {
    if (!authCurrentUser?.uid) {
      return
    }

    setLoading(true)
    let snapshotReceived = false

    const unsubscribe = onSnapshot(
      doc(db, 'users', authCurrentUser.uid),
      (snapshot) => {
        snapshotReceived = true
        if (snapshot.exists()) {
          const data = snapshot.data()
          const userData = { ...authCurrentUser, ...data } as unknown as User
          setCurrentUser(userData)
          setSession('data', userData)
          setLoading(false)
        } else {
          // Document doesn't exist yet (new Okta user being provisioned)
          console.log('User document not found yet, waiting for provisioning...')
        }
      },
      (error) => {
        console.error('User snapshot error:', error)
        snapshotReceived = true
        if (!provisioningWarningShown.current) {
          handleError('Unable to load your profile data. Please try again.')
          provisioningWarningShown.current = true
        }
        setLoading(false)
      }
    )

    // Fallback only for provisioning delay (Okta new users)
    const fallbackTimer = setTimeout(() => {
      if (snapshotReceived) return

      // Snapshot hasn't fired yet - likely new Okta user being provisioned
      console.log('User document not ready after 8s, showing provisioning message...')
      if (!provisioningWarningShown.current) {
        handleError('We are still setting up your Loop account. This may take a few moments.')
        provisioningWarningShown.current = true
      }
      // Keep loading state active, let snapshot handle completion
    }, 8000)

    return () => {
      unsubscribe()
      clearTimeout(fallbackTimer)
    }
  }, [authCurrentUser?.uid, handleError])

  // Check session cookie on initial load for SSO
  // Only runs on *.tryloop.ai domains (cross-subdomain SSO)
  useEffect(() => {
    // Skip on localhost/preview - SSO only works on *.tryloop.ai
    const isProductionDomain = window.location.hostname.endsWith(PRODUCTION_DOMAIN)
    if (!isProductionDomain) return

    const checkSessionCookie = async () => {
      try {
        const res = await checkAuthStatus()

        if (res && res.success) {
          const customToken = res.token
          await signInWithCustomToken(auth, customToken)
          // User will be authenticated via onAuthStateChanged
        }
      } catch (err) {
        console.error('Session check failed:', err)
      }
    }

    // Skip session cookie check if magic_token is present - let magic link flow handle auth
    const hasMagicToken = params.get('magic_token')
    if (hasMagicToken) {
      return
    }

    // Only check session cookie if user is not already authenticated
    if (!authCurrentUser && !magicLinkLoading) {
      checkSessionCookie()
    }
  }, [authCurrentUser, magicLinkLoading, params])

  // Cross-subdomain auth sync via visibility/focus events
  // This handles both login AND logout sync between app.tryloop.ai and admin.tryloop.ai
  // Only runs on *.tryloop.ai domains (cross-subdomain SSO)
  useEffect(() => {
    // Skip on localhost/preview - SSO only works on *.tryloop.ai
    const isProductionDomain = window.location.hostname.endsWith(PRODUCTION_DOMAIN)
    if (!isProductionDomain) return

    let lastCheck = 0
    const DEBOUNCE_MS = 2000 // Prevent rapid checks

    const checkAuthOnVisibility = async () => {
      const now = Date.now()
      if (now - lastCheck < DEBOUNCE_MS) return
      lastCheck = now

      // Skip if magic_token is present
      if (params.get('magic_token')) return

      try {
        const res = await checkAuthStatus()
        if (res && res.success) {
          // User is authenticated on another subdomain
          if (!authCurrentUser) {
            // Sign in if not already logged in here
            const customToken = res.token
            await signInWithCustomToken(auth, customToken)
          }
        } else if (authCurrentUser) {
          // Session cookie is gone but user is still logged in here
          // This means they logged out on another subdomain - sync the logout
          console.debug('Session cookie cleared on another subdomain, signing out...')
          await signOut(auth)
          setAuthCurrentUser(null)
          setCurrentUser(undefined)
          clearLocalData()
        }
      } catch (err: any) {
        console.debug('Cross-subdomain auth check error:', err)
      }
    }

    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        checkAuthOnVisibility()
      }
    }

    const handleFocus = () => {
      checkAuthOnVisibility()
    }

    document.addEventListener('visibilitychange', handleVisibilityChange)
    window.addEventListener('focus', handleFocus)

    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange)
      window.removeEventListener('focus', handleFocus)
    }
  }, [authCurrentUser, params])

  useEffect(() => {
    const magicLinkFromAdmin = getSession('magic_link_from_admin') || false
    if (magicLinkFromAdmin) {
      setMagicLinkFromAdmin(true)
    } else {
      setMagicLinkFromAdmin(false)
    }
  }, [])

  useEffect(() => {
    const signInIfMagicLink = async () => {
      // Skip if user is already authenticated
      if (currentUser) {
        // Clean up magic_token from URL if present
        const token = params.get('magic_token')
        if (token) {
          setParams((prev) => {
            prev.delete('magic_token')
            prev.delete('reference')
            return prev
          })
        }
        return
      }

      try {
        const token = params.get('magic_token')?.replace(/\s/g, '+')
        const fromAdmin = params.get('reference') === 'admin'
        if (!token) {
          return
        } else {
          setMagicLinkLoading(true)
        }

        console.debug('[Magic Link] Validating token...')
        const res = await validateUserToken({ tokenList: [token], getAuthToken: true })
        console.log('[Magic Link] Validation response:', { expired: get(res, '[0].expired'), hasAuthToken: !!get(res, '[0].data.authToken') })

        if (get(res, '[0].expired', true)) {
          console.log('[Magic Link] Token expired, redirecting to /login/expired')
          navigate('/login/expired')
          setMagicLinkLoading(false)
          return
        }

        const authToken = get(res, '[0].data.authToken', null) as string | null
        if (authToken) {
          metricsService.trackUserLoginWithMagicLink()
          setParams((prev) => {
            prev.delete('magic_token')
            prev.delete('reference')
            return prev
          })
          if (fromAdmin) {
            setMagicLinkFromAdmin(true)
            setSession('magic_link_from_admin', true)
            posthog.capture('magic_link_identifier', { fromAdmin })
          } else {
            setMagicLinkFromAdmin(false)
          }

          console.log('[Magic Link] Signing in with custom token...')
          await signInWithCustomToken(auth, authToken)
          console.log('[Magic Link] Sign in successful')
          setLoading(true)
        } else {
          console.error('[Magic Link] No authToken received from validation')
          handleError('Failed to authenticate with magic link. Please try again.', { silent: false })
        }
        setMagicLinkLoading(false)
      } catch (err: any) {
        console.error('[Magic Link] Error during authentication:', err?.message || err)
        handleError('Magic link authentication failed. Please request a new link.', { silent: false })
        setMagicLinkLoading(false)
      }
    }
    signInIfMagicLink()
  }, [params, currentUser])

  const contextValue = useMemo(() => {
    return {
      currentUser,
      authCurrentUser,
      pageDetails,
      loading,
      isLoggingOut,
      magicLinkFromAdmin,
      setLoading,
      setPageDetails,
      analyticsReset,
      logout
    }
  }, [currentUser, authCurrentUser, magicLinkFromAdmin, isLoggingOut, pageDetails, loading])

  return (
    <>
      <PublicAuthContext.Provider value={contextValue}>{props.children}</PublicAuthContext.Provider>
    </>
  )
}
